import { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Globe, Loader2, CheckCircle2, XCircle } from 'lucide-react';

const MethodBadge = ({ method }: { method?: string }) => {
    const m = (method || 'GET').toUpperCase();
    const colors: Record<string, string> = {
        GET: 'bg-blue-100 text-blue-700 border-blue-200',
        POST: 'bg-green-100 text-green-700 border-green-200',
        PUT: 'bg-orange-100 text-orange-700 border-orange-200',
        DELETE: 'bg-red-100 text-red-700 border-red-200',
        PATCH: 'bg-purple-100 text-purple-700 border-purple-200'
    };
    return (
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${colors[m] || 'bg-gray-100 text-gray-700'}`}>
            {m}
        </span>
    );
};

export const UniversalApiNode = memo(({ data, selected }: any) => {
    return (
        <div className={`
        w-[200px] bg-white rounded-lg shadow-sm border transition-all duration-200 relative
            ${selected ? 'ring-2 ring-fuchsia-500 border-fuchsia-500 shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow'}
        `}>
            {/* Header */}
            <div className={`
                h-8 px-2 flex items-center gap-2 rounded-t-lg bg-gradient-to-r from-fuchsia-50 to-white border-b border-fuchsia-100
            `}>
                <div className="p-1 rounded bg-white shadow-sm border border-fuchsia-100">
                    <Globe className="w-3.5 h-3.5 text-fuchsia-600" />
                </div>
                <span className="text-xs font-semibold text-slate-700 truncate flex-1">
                    {data.label || 'API Call'}
                </span>
            </div>

            {/* Status Icons — exact same pattern as BaseNodeUI */}
            {(data.executionStatus === 'RUNNING' || data.executionStatus === 'Running') && (
                <div className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md z-20 border border-slate-200">
                    <Loader2 className="h-3.5 w-3.5 text-blue-600 animate-spin" strokeWidth={2.5} />
                </div>
            )}
            {(data.executionStatus === 'COMPLETED' || data.executionStatus === 'Success') && (
                <div className="absolute -top-2 -right-2 p-1 bg-green-600 rounded-full shadow-md z-20 border border-white">
                    <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                </div>
            )}
            {(data.executionStatus === 'FAILED' || data.executionStatus === 'Failed') && (
                <div className="absolute -top-2 -right-2 p-1 bg-red-600 rounded-full shadow-md z-20 border border-white">
                    <XCircle className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                </div>
            )}

            {/* Content */}
            <div className="p-2 space-y-2">
                <div className="flex items-center justify-between">
                    <MethodBadge method={data.method} />
                    <span className="text-[9px] text-slate-400 font-mono">JSON</span>
                </div>

                {data.url ? (
                    <div className="bg-slate-50 border border-slate-100 rounded px-1.5 py-1">
                        <p className="text-[9px] text-slate-500 font-mono truncate" title={data.url}>
                            {data.url.replace(/^https?:\/\//, '')}
                        </p>
                    </div>
                ) : (
                    <div className="text-[10px] text-slate-400 italic text-center py-1">
                        No Endpoint
                    </div>
                )}
            </div>

            {/* Handles */}
            <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-slate-300 !border-white" />
            <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-slate-300 !border-white" />
        </div>
    );
});
